import { SimpleModel } from 'model/SimpleModel';

import { Logic } from './Logic';
import { LogicType } from './LogicType';
import { NumberingSkippable } from './NumberingSkippable';
import { PageItemKind } from './PageItemKind';

export namespace PageItem {
  export interface Shape {
    id?: number;
    position?: number;
    kind?: PageItemKind;
    logic?: Logic;
    [key: string]: unknown;
  }
}

export abstract class PageItem<S extends PageItem.Shape = PageItem.Shape>
  extends SimpleModel<S>
  implements NumberingSkippable
{
  static readonly QUESTION_KINDS: PageItemKind[] = [
    PageItemKind.QuestionOpinion,
    PageItemKind.QuestionRadioButton,
    PageItemKind.QuestionCheckbox,
    PageItemKind.QuestionCAS,
    PageItemKind.QuestionCommentsBox,
    PageItemKind.QuestionForm,
    PageItemKind.QuestionDemographic,
  ];

  static readonly KINDS: PageItemKind[] = [
    ...PageItem.QUESTION_KINDS,
    PageItemKind.TextWidget,
    PageItemKind.ImageWidget,
    PageItemKind.HeaderWidget,
  ];

  static readonly LOGICS: LogicType[] = [
    LogicType.Dependency,
    LogicType.Classification,
    LogicType.Distribution,
  ];

  constructor(initialData: SimpleModel.Data<S>, kind?: PageItemKind) {
    // Convert initialData into a plain object if it's a Map
    const data = initialData instanceof Map ? Object.fromEntries(initialData) : initialData;

    // Create the model with the optional kind included
    super(kind ? { ...data, kind } : data);
  }

  getId(): number {
    return this.get('id');
  }

  hasLogic(): boolean {
    return !!this.get('logic');
  }

  setId(id: number): this {
    return this.set('id', id);
  }

  getPosition(): number {
    return this.get('position');
  }

  setPosition(position: number): this {
    return this.set('position', position);
  }

  getKind(): PageItemKind {
    return this.get('kind');
  }

  getGeneralKind(): PageItemKind.General {
    return PageItemKind.getGeneralKind(this.getKind());
  }

  getLogic(): Logic {
    return this.get('logic');
  }

  isNumberingSkipped(): boolean {
    return false;
  }

  copy(): this {
    return this.set('id', undefined).set('logic', undefined);
  }
}
